<script setup>
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { useSubscriptionStore } from '@organisms/ubiq-administration/store/subscriptions';
import {
  URL_INVOICING_HEADER_INFOS,
  URL_USER_ORGANIZATION_PAYMENT_ACCOUNT,
} from '../../../_00_elements/tools/routes/routes';
import { trans } from '../../../_00_elements/translation';
import { EMBEDDED_COMPONENTS } from '../general/embedded-constants';
import { useAccountStore } from '../store/account';
import MenuTabs from '../../menu/MenuTabs.vue';

const updatingAccount = ref(false);

const fetchClientSecret = async () => {
  const subscriptionStore = useSubscriptionStore();
  await subscriptionStore.getOnboardingClientSecret();
  return subscriptionStore.onboardingClientSecret;
};

const updateAccount = async () => {
  updatingAccount.value = true;
  const stripeConnectInstance = await loadConnectAndInitialize({
    publishableKey: window.STRIPE_API_KEY,
    fetchClientSecret,
  });

  const onboardingComponent = stripeConnectInstance.create(EMBEDDED_COMPONENTS.ACCOUNT_MANAGEMENT);
  const container = document.getElementById('connect-management');
  if (container) {
    container.style.width = '100%';
    container.style.height = '800px';
    container.appendChild(onboardingComponent);
  }
};

const accountStore = useAccountStore();
const { account, statusFormatted, stripeAccountStatus } = storeToRefs(accountStore);

const labelStatus = computed(() => trans(`gestion.organization.account.${statusFormatted.value}`));
const iban = computed(() => {
  if (!account.value?.external_accounts || account.value.external_accounts.data.length === 0) {
    return null;
  }
  return `XXXX XXXX XXXX XXXX XXXX XXXX ${account.value.external_accounts.data[0].last4}`;
});
const statusClass = computed(() => (statusFormatted.value.includes('limited') ? 'status-label--limited' : `status-label--${statusFormatted.value}`));
const navigationTabs = [
  { name: trans('organization.settings.header_invoices'), path: URL_INVOICING_HEADER_INFOS },
  { name: trans('organization.settings.payment_account'), path: URL_USER_ORGANIZATION_PAYMENT_ACCOUNT },
];
</script>

<template>
  <div class="payment-account">
    <MenuTabs :tabs="navigationTabs" :title="trans('organization.settings.title')" />

    <div v-if="!updatingAccount" class="payment-account-status">
      <div class="payment-account-status__label">
        <p>{{ trans('gestion.organization.account.state') }}</p>
        <span v-if="stripeAccountStatus" class="status-label" :class="statusClass">{{ labelStatus }}</span>
      </div>
      <p v-if="!account" class="payment-account-status-none">
        {{ trans('gestion.organization.account.no_account') }}
      </p>
      <hr>
      <div class="payment-account-iban__label">
        <p>{{ trans('gestion.organization.account.payout') }}</p>
      </div>
      <div v-if="iban" class="payment-account-iban-info">
        <input id="iban" type="text" disabled :value="iban">
      </div>
      <p v-else class="payment-account-iban-none">
        {{ trans('gestion.organization.account.no_iban') }}
      </p>
      <button
        class="card__button btn btn--primary btn--small"
        @click="updateAccount"
      >
        {{ trans('gestion.organization.account.buttons') }}
      </button>
    </div>
    <div v-if="updatingAccount" id="connect-management" />
  </div>
</template>
